import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { PageHero } from "../components/PageHero";
import { CtaBlock } from "../components/CtaBlock";
import { FaqAccordion, buildFaqLd } from "../components/FaqAccordion";
import { buildBreadcrumbLd } from "../components/Breadcrumbs";
import { Picture, findPhoto } from "../lib/photos";
import { useSeo } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AmenityDetail() {
  const { slug } = useParams();
  const [amenity, setAmenity] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    let alive = true;
    setNotFound(false);
    setAmenity(null);
    (async () => {
      try {
        const [a, rs, as, pp] = await Promise.all([
          axios.get(`${API}/amenities/${slug}`).then((r) => r.data).catch(() => null),
          axios.get(`${API}/rooms`).then((r) => r.data),
          axios.get(`${API}/amenities`).then((r) => r.data),
          axios.get(`${API}/photos`).then((r) => r.data),
        ]);
        if (!alive) return;
        if (!a) {
          setNotFound(true);
          return;
        }
        setAmenity(a);
        setRooms(rs || []);
        setAmenities(as || []);
        setPhotos(pp.photos || []);
      } catch (e) {
        console.error("amenity detail fetch fail", e);
        setNotFound(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  const hero = useMemo(
    () => (amenity && photos.length ? findPhoto(photos, amenity.photo_slug) : null),
    [amenity, photos],
  );
  const gallery = useMemo(
    () =>
      amenity
        ? (amenity.photo_slugs || []).map((s) => findPhoto(photos, s)).filter(Boolean)
        : [],
    [amenity, photos],
  );

  const relatedRooms = useMemo(
    () =>
      amenity && rooms.length
        ? (amenity.related_rooms || [])
            .map((slug) => rooms.find((r) => r.slug === slug))
            .filter(Boolean)
        : [],
    [amenity, rooms],
  );

  const relatedAmenities = useMemo(
    () =>
      amenity && amenities.length
        ? (amenity.related_amenities || [])
            .map((slug) => amenities.find((a) => a.slug === slug))
            .filter(Boolean)
        : [],
    [amenity, amenities],
  );

  const canonical = amenity ? `https://westbridgehotels.com/amenities/${amenity.slug}` : undefined;
  useSeo({
    title: amenity?.meta_title || "Amenity — Westbridge Inn & Suites",
    description: amenity?.meta_description || "",
    canonical,
    image: hero ? `${window.location.origin}${hero.jpg}` : undefined,
    jsonLd: amenity
      ? [
          buildBreadcrumbLd([
            { label: "Amenities", to: "/amenities" },
            { label: amenity.label, to: `/amenities/${amenity.slug}` },
          ]),
          buildFaqLd(amenity.faqs),
        ]
      : [],
  });

  if (notFound) {
    return (
      <main id="main" className="pt-40 pb-24 min-h-[60vh] bg-cream">
        <div className="container-app text-center">
          <p className="eyebrow text-brass mb-3">404</p>
          <h1 className="font-display text-4xl md:text-5xl text-forest-900 mb-4">
            Amenity not found
          </h1>
          <Link to="/amenities" className="btn-ghost-dark">See all amenities</Link>
        </div>
      </main>
    );
  }

  if (!amenity) {
    return (
      <main id="main" className="pt-40 pb-24 min-h-[60vh] bg-cream">
        <div className="container-app">
          <div className="w-full h-72 bg-tan-200 animate-pulse" />
        </div>
      </main>
    );
  }

  const Icon = Icons[amenity.icon] || Icons.Sparkles;
  const isPoolShort = amenity.slug === "indoor-pool";

  return (
    <main id="main" data-testid="amenity-detail">
      <PageHero
        eyebrow="Amenity"
        title={amenity.label}
        intro={amenity.tagline}
        photo={hero}
        breadcrumbs={[
          { label: "Amenities", to: "/amenities" },
          { label: amenity.label, to: `/amenities/${amenity.slug}` },
        ]}
        testId="amenity-hero"
      />

      <section className="bg-cream py-16 md:py-24">
        <div className="container-app grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-8 max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-10 grid place-items-center rounded-full bg-brass/15 text-brass">
                <Icon size={18} strokeWidth={1.75} />
              </span>
              <p className="eyebrow">{amenity.label}</p>
            </div>
            <div className="space-y-5 text-ink/85 text-lg leading-relaxed">
              {amenity.body_paragraphs?.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            {isPoolShort && (
              <div className="mt-8 bg-cream-100 border border-brass/40 p-6">
                <p className="text-forest-900">
                  Want the full story on our indoor pool — schedule, family angle, ADA lift?{" "}
                  <Link to="/indoor-pool" className="brass-underline font-medium">
                    Read the Indoor Pool guide →
                  </Link>
                </p>
              </div>
            )}
          </div>

          <aside className="md:col-span-4 bg-cream-100 border border-tan p-6 md:p-8">
            <p className="eyebrow mb-4">Highlights</p>
            <ul className="space-y-3 text-sm">
              {amenity.highlights?.map((h) => (
                <li key={h} className="flex gap-2 text-ink/85">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brass shrink-0" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
            {amenity.policy_notes?.length > 0 && (
              <>
                <p className="eyebrow mt-8 mb-4">Good to know</p>
                <ul className="space-y-3 text-sm">
                  {amenity.policy_notes.map((p, i) => (
                    <li key={i} className="text-ink/70 leading-relaxed">— {p}</li>
                  ))}
                </ul>
              </>
            )}
          </aside>
        </div>
      </section>

      {/* Extra gallery for the amenity if 2+ photos */}
      {gallery.length > 1 && (
        <section className="bg-cream-100 py-14 md:py-20 paper-grain" data-testid="amenity-gallery">
          <div className="container-app relative grid md:grid-cols-2 gap-4">
            {gallery.slice(0, 2).map((p) => (
              <Picture
                key={p.slug}
                photo={p}
                className="block w-full aspect-[16/10] overflow-hidden"
                imgClassName="w-full h-full object-cover"
                sizes="(min-width: 768px) 45vw, 100vw"
              />
            ))}
          </div>
        </section>
      )}

      {/* Related rooms */}
      {relatedRooms.length > 0 && (
        <section className="bg-cream py-16 md:py-20" data-testid="amenity-related-rooms">
          <div className="container-app">
            <p className="eyebrow mb-3">Related rooms</p>
            <h2 className="font-display font-light text-2xl md:text-4xl text-forest-900 mb-8 tracking-tight">
              Best rooms for this amenity.
            </h2>
            <div className="grid md:grid-cols-3 gap-5">
              {relatedRooms.map((r) => (
                <Link
                  key={r.slug}
                  to={`/rooms/${r.slug}`}
                  className="group border border-tan bg-cream-50 p-6 hover:border-brass/60 transition-colors"
                  data-testid={`amenity-related-room-${r.slug}`}
                >
                  <p className="eyebrow text-brass mb-2">
                    {r.corridor === "interior" ? "Interior" : "Exterior"}
                  </p>
                  <h3 className="font-display text-xl text-forest-900 mb-2 group-hover:text-brass">
                    {r.name}
                  </h3>
                  <p className="text-sm text-ink/75 leading-relaxed">{r.short}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related amenities */}
      {relatedAmenities.length > 0 && (
        <section className="bg-cream-100 py-12 md:py-16 paper-grain">
          <div className="container-app relative">
            <p className="eyebrow mb-4">Also worth reading</p>
            <ul className="flex flex-wrap gap-3">
              {relatedAmenities.map((a) => (
                <li key={a.slug}>
                  <Link
                    to={`/amenities/${a.slug}`}
                    className="inline-flex items-center px-4 py-2 rounded-full border border-forest/30 text-forest-900 text-sm hover:bg-forest hover:text-cream transition-colors"
                    data-testid={`amenity-related-${a.slug}`}
                  >
                    {a.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* FAQ */}
      {amenity.faqs?.length > 0 && (
        <section className="bg-cream py-16 md:py-24" data-testid="amenity-faq">
          <div className="container-app grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="eyebrow mb-3">Common questions</p>
              <h2 className="font-display font-light text-2xl md:text-4xl text-forest-900 tracking-tight leading-tight">
                About {amenity.label.toLowerCase()}.
              </h2>
            </div>
            <div className="md:col-span-8">
              <FaqAccordion items={amenity.faqs} testIdPrefix="amenity-faq" />
            </div>
          </div>
        </section>
      )}

      <CtaBlock />
    </main>
  );
}
